<template>
  <b-row
    v-if="!category.deleted"
    :class="selected ? 'd-flex selected category-list-item' : 'd-flex category-list-item'"
    @click="clickCategory"
    no-gutters
  >
    <!-- カテゴリー名 -->
    <div class="p-2 my-auto category-name">{{ title }}</div>

    <div class="d-flex p-2 ml-auto flex-direction-row color-palette">
      <!-- カテゴリ作成者アイコン -->
      <div :id="category.id" class="owner-icon">
        <profile-icon
          class="rounded-circle m-auto"
          :userName="owner.name"
          :id="owner.directId"
          :image="owner.getProfileIconUrl()"
          :size="25"
        />
      </div>
      <!--カテゴリ作成者名popover-->
      <b-popover
        :target="category.id"
        :content="owner.name"
        triggers="hover"
        placement="top"
        boundary="viewport"
      ></b-popover>

      <!-- 編集ボタン -->
      <b-button
        v-if="isEdit"
        variant="info"
        class="edit-button"
        v-on:click="clickEditButton"
      ></b-button>

      <!-- 色設定ボタン -->
      <b-dropdown
        v-if="isEdit"
        class="category-color-picker"
        :toggle-class="toggleClass"
        menu-class="p-0"
        right
      >
        <b-dropdown-group class="p-0">
          <b-list-group v-for="(colors, i) in colorGroup" :key="i" horizontal>
            <b-list-group-item class="color-item" v-for="(color, j) in colors" :key="j">
              <b-button
                :class="`${color} color-item-button`"
                :pressed="isPressed(i * 4 + j)"
                v-on:click="press(i * 4 + j)"
              >
              </b-button>
            </b-list-group-item>
          </b-list-group>
        </b-dropdown-group>
      </b-dropdown>
    </div>
  </b-row>
</template>
<style lang="scss" scoped>
$category-list-item-select-bgcolor: #d6eef5;

.category-list-item {
  cursor: pointer;
  &:hover {
    background-color: rgba($category-list-item-select-bgcolor, 0.5);
  }
  &.selected {
    background-color: $category-list-item-select-bgcolor;
    flex-wrap: nowrap;
  }
}

.category-name {
  flex: 1 1 0;
  font-size: 14px;
  text-overflow: ellipsis;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
}

.edit-button {
  margin-right: 8px;
  white-space: nowrap;

  &::before {
    content: "変更";
  }
  @media (min-width: 992px) {
    // lg 〜
    &::before {
      content: "名前変更";
    }
  }
}

.color-dropdown {
  border-radius: 50%;
}

.color-item {
  padding: 5px;
  border-style: none;
}

.color-item-button {
  width: 39px;
  height: 23px;
  border-width: 3px;
  border-radius: 8px;
}

.owner-icon {
  display: flex;
  margin-right: 8px;
}
</style>

<style lang="scss">
.category-color-picker .dropdown-toggle {
  &::before {
    content: "色";
  }
  @media (min-width: 768px) {
    // md 〜
    &::before {
      content: "色変更";
    }
  }
}
</style>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from "vue";
import { useDomainStore } from "@/store";
import { Category, User } from "../model";
import { COLOR_PALETTE, DEFAULT_COLOR } from "./color-palette";
import ProfileIcon from "./ProfileIcon.vue";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";
import "./color-palette.scss";

export default defineComponent({
  name: "category-list-item",
  components: {
    ProfileIcon,
  },
  props: {
    category: {
      type: Object as PropType<Category>,
      default: () => Category.createTemp(""),
      required: true,
    },
    selected: { type: Boolean, default: () => false },
    isMobile: { type: Boolean, default: () => false, required: true },
    // ログインユーザー情報
    viewer: { type: Object as PropType<User>, default: () => User.createNotFoundUser() },
    // カテゴリ作成者情報
    owner: { type: Object as PropType<User>, default: () => User.createNotFoundUser() },
  },
  setup(props, { emit }) {
    const rootEvent = useLegacyRootEvent();

    // 色選択パレット関連
    const colorPalette = ref<string[]>(COLOR_PALETTE);
    const defaultColor = ref<string>(DEFAULT_COLOR);
    const pressedIndex = ref<number>(-1); // -1 : デフォルトカラーになる, それ以外: そのボタンが押されている

    watch(
      () => props.category,
      (value: Category): void => {
        const color = value.color || defaultColor.value;
        const index = colorPalette.value.findIndex((c) => c == color);
        if (index < 0) {
          const defaultIndex = colorPalette.value.findIndex((c) => c == defaultColor.value);
          pressedIndex.value = defaultIndex;
        } else {
          pressedIndex.value = index;
        }
      },
      { immediate: true }
    );

    const title = computed((): string => {
      return props.category.title;
    });

    const colorGroup = computed((): string[][] => {
      const rows = Math.ceil(colorPalette.value.length / 4);
      const group = new Array(rows).fill([]);
      const result = group.map((_, i) => {
        return colorPalette.value.slice(i * 4, (i + 1) * 4);
      });
      return result;
    });

    const toggleClass = computed((): string => {
      return colorPalette.value[pressedIndex.value] || defaultColor.value;
    });

    const isEdit = computed((): boolean => {
      const role = useDomainStore().getDomainRole;
      const roleId = role ? role.type : 30; // 30:ユーザー
      return props.viewer.id == props.category.owner || roleId == 10 || roleId == 20; // role.type 10:所有者 20:管理者
    });

    const isPressed = (index: number): boolean => {
      return index == pressedIndex.value;
    };

    // カラーの選択
    // => CategoryEdit
    const onEditColor = (): void => {
      rootEvent.emit("edit-category-color", { id: props.category.id, color: toggleClass.value });
    };

    const press = (index: number): void => {
      pressedIndex.value = index;
      onEditColor();
    };

    // カテゴリーの選択
    // => CategoryList
    const clickCategory = (): void => {
      emit("clickCategory", props.category.id);
    };

    // カテゴリー編集ボタンクリック
    // => CategoryEdit (=>CategoryTitleEdit)
    const clickEditButton = (): void => {
      rootEvent.emit("edit-category-title", { id: props.category.id });
    };

    return {
      colorPalette,
      defaultColor,
      pressedIndex,
      title,
      colorGroup,
      toggleClass,
      isEdit,
      isPressed,
      onEditColor,
      press,
      clickCategory,
      clickEditButton,
    };
  },
});
</script>
